import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Home as HomeIcon, // eslint-disable-line no-unused-vars
  ShieldCheck,
  Users,
  Languages,
  Star,
  ChevronRight,
  Mic,
  Image as ImageIcon,
  Ruler,
  BedDouble,
  Sparkles,
  Package,
  MapPin,
  Compass,
  Download,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";

import LanguageToggle from "@/components/LanguageToggle";
import { ChatBubble, TypingDots } from "@/components/ChatBubble";
import SelectionCard from "@/components/SelectionCard";
import BeforeAfter from "@/components/BeforeAfter";
import SavingsCalculator from "@/components/SavingsCalculator";
import { trackEvent, getStoredUtmParams } from "@/utils/analytics";
import LegalDisclaimer from "@/components/LegalDisclaimer";
import BrandLogo from "@/components/BrandLogo";
import { ModernHouseSVG } from "@/components/HouseSVG";
import FreeSampleBanner from "@/components/FreeSampleBanner";
import { PUBLIC_MITRA_BUCKETS } from "@/mitras/mitraCategories";
import { FINANCE_MITRA_ENABLED } from "@/lib/featureFlags";
import { t } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Dimension presets (Length × Width) — keeps Plot Dimensions prominent
const PLOT_OPTIONS = [
  { id: "20x30", label: "20×30", sqft: 600, captionKey: "plot_xs" },
  { id: "20x40", label: "20×40", sqft: 800, captionKey: "plot_s" },
  { id: "25x50", label: "25×50", sqft: 1250, captionKey: "plot_s" },
  { id: "30x50", label: "30×50", sqft: 1500, captionKey: "plot_m" },
  { id: "30x60", label: "30×60", sqft: 1800, captionKey: "plot_m" },
  { id: "40x60", label: "40×60", sqft: 2400, captionKey: "plot_l" },
];

const FACING_OPTIONS = [
  { id: "north", labelKey: "facing_north", angle: 0 },
  { id: "east", labelKey: "facing_east", angle: 90 },
  { id: "south", labelKey: "facing_south", angle: 180 },
  { id: "west", labelKey: "facing_west", angle: 270 },
];

const ROOM_OPTIONS = [
  { id: "1", label: "1", sub: "BHK" },
  { id: "2", label: "2", sub: "BHK" },
  { id: "3", label: "3", sub: "BHK" },
  { id: "4+", label: "4+", sub: "BHK" },
];

// Mera Naksha business WhatsApp number — single source of truth.
const WA_PHONE = "919717667115";
const WA_DISPLAY = "+91 97176 67115";

// Placeholder WhatsApp Community invite — swap with the real chat.whatsapp.com URL.
const COMMUNITY_URL = "https://chat.whatsapp.com/INVITE-PLACEHOLDER";

const PlotIcon = ({ size = 36 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 48 48"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="6" y="6" width="36" height="36" rx="3" />
    <path d="M6 18 L42 18 M18 6 L18 42 M30 18 L30 42" opacity="0.5" />
  </svg>
);

// Compass with arrow rotated towards the direction.
const CompassFacingIcon = ({ angle = 0, size = 40 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 48 48"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="24" cy="24" r="20" />
    <text x="24" y="11.5" textAnchor="middle" fontSize="7" fontWeight="700" fill="currentColor" stroke="none">N</text>
    <text x="24" y="44" textAnchor="middle" fontSize="7" fontWeight="700" fill="currentColor" stroke="none">S</text>
    <text x="42.5" y="26" textAnchor="middle" fontSize="7" fontWeight="700" fill="currentColor" stroke="none">E</text>
    <text x="5.5" y="26" textAnchor="middle" fontSize="7" fontWeight="700" fill="currentColor" stroke="none">W</text>
    <g transform={`rotate(${angle} 24 24)`}>
      <path d="M24 9 L28 24 L24 21 L20 24 Z" fill="currentColor" stroke="none" />
      <path d="M24 39 L20 24 L24 27 L28 24 Z" opacity="0.3" fill="currentColor" stroke="none" />
    </g>
  </svg>
);

const Landing = () => {
  const [lang, setLang] = useState("hi");
  const [plot, setPlot] = useState(null);
  const [facing, setFacing] = useState(null);
  const [rooms, setRooms] = useState(null);
  const [storeCount, setStoreCount] = useState(null);

  const canContinue = plot && facing && rooms;

  useEffect(() => {
    // Homepage viewed — annotate with UTM params captured at app boot so
    // GA4 attribution shows which campaign the visitor came from.
    trackEvent("homepage_viewed", {
      referrer: document.referrer || "direct",
      ...getStoredUtmParams(),
    });
    let cancel = false;
    axios
      .get(`${API}/library?page=1&page_size=1`)
      .then(({ data }) => {
        if (!cancel) setStoreCount(data.total);
      })
      .catch(() => {});
    return () => {
      cancel = true;
    };
  }, []);

  const onWhatsApp = () => {
    const plotOpt = PLOT_OPTIONS.find((p) => p.id === plot);
    const facingOpt = FACING_OPTIONS.find((f) => f.id === facing);
    const facingLabel = facingOpt ? t(lang, facingOpt.labelKey) : "—";
    const plotLabel = plotOpt ? `${plotOpt.label} (${plotOpt.sqft} sqft)` : "—";
    const msg =
      lang === "hi"
        ? `नमस्ते 🙏 मुझे नक्शा चाहिए।\nप्लॉट: ${plotLabel}\nदिशा: ${facingLabel}\nकमरे: ${rooms || "—"} BHK`
        : `Hi 🙏 I need a house plan.\nPlot: ${plotLabel}\nFacing: ${facingLabel}\nRooms: ${rooms || "—"} BHK`;
    window.open(
      `https://wa.me/${WA_PHONE}?text=${encodeURIComponent(msg)}`,
      "_blank",
      "noopener"
    );
  };

  const stepProgress = useMemo(() => {
    let p = 0;
    if (plot) p += 34;
    if (facing) p += 33;
    if (rooms) p += 33;
    return p;
  }, [plot, facing, rooms]);

  return (
    <div className="min-h-screen bg-sand" data-testid="landing-root">
      {/* Top bar — navy on white */}
      <header
        className="sticky top-0 z-30 backdrop-blur-md bg-white/90 border-b border-border"
        data-testid="site-header"
      >
        <div className="mx-auto max-w-md sm:max-w-3xl px-4 h-16 flex items-center justify-between">
          <BrandLogo size="md" to="/" withTagline lang={lang} testId="landing-brand-logo" />
          <div className="flex items-center gap-2">
            <Link
              to="/store"
              data-testid="nav-store-btn"
              className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-gold text-navy text-xs font-bold border border-gold-dark hover:bg-gold-dark hover:text-white transition-colors"
            >
              <Download className="h-3.5 w-3.5" />
              <span>{t(lang, "store_kicker")}</span>
            </Link>
            <Link
              to="/design-studio"
              data-testid="nav-design-studio-btn"
              className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-navy text-gold text-xs font-extrabold border border-navy-600 hover:bg-navy-600 transition-colors"
            >
              <Sparkles className="h-3.5 w-3.5" />
              Design Studio
            </Link>
            <Link
              to="/mitras"
              data-testid="nav-mitras-btn"
              className="hidden md:inline-flex items-center gap-1.5 h-9 px-3 rounded-full border-2 border-navy text-navy text-xs font-extrabold hover:bg-navy hover:text-gold transition-colors"
            >
              Find Mitras
            </Link>
            <Link
              to="/about"
              data-testid="nav-about-btn"
              className="hidden md:inline-flex h-9 items-center px-3 rounded-full border border-navy/15 text-navy text-xs font-bold hover:bg-navy hover:text-gold transition-colors"
            >
              About Us
            </Link>
            <a
              href={COMMUNITY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-community-btn"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-whatsapp text-white text-xs font-bold shadow-[0_6px_14px_rgba(37,211,102,0.35)] hover:bg-whatsapp-hover transition-colors"
            >
              <FaWhatsapp className="h-4 w-4" />
              <span>{t(lang, "community_nav")}</span>
            </a>
            <Link
              to="/admin"
              data-testid="admin-link"
              className="hidden sm:inline-flex h-9 items-center px-3 rounded-full border border-navy/15 text-navy text-xs font-bold hover:bg-navy hover:text-gold transition-colors"
            >
              Admin
            </Link>
            <LanguageToggle lang={lang} onChange={setLang} />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-md sm:max-w-3xl px-4 pb-40 sm:pb-32">
        {/* HERO — Aspirational, deep navy panel with gold accents */}
        <section
          className="mt-5 relative overflow-hidden rounded-3xl bg-navy-jaali text-white shadow-[0_30px_60px_-30px_rgba(11,37,69,0.55)] animate-fade-up"
          data-testid="hero-section"
        >
          {/* Gold accent bar */}
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-gold-dark via-gold to-gold-dark" />
          <div className="absolute -right-10 -bottom-10 w-56 h-56 rounded-full bg-gold/10 blur-2xl pointer-events-none" />

          <div className="p-5 sm:p-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold/15 border border-gold/30 text-gold text-[11px] font-bold tracking-wider uppercase">
              <Sparkles className="h-3.5 w-3.5" />
              {t(lang, "hero_eyebrow")}
            </div>

            <h1 className="mt-3 text-[30px] sm:text-5xl font-bold leading-[1.08] tracking-tight text-white text-balance">
              {t(lang, "hero_title_aspire_1")}
              <br />
              <span className="gold-shimmer animate-shimmer">
                {t(lang, "hero_title_aspire_2")}
              </span>
            </h1>
            <p className="mt-3 text-[15px] sm:text-base text-white/75 leading-relaxed max-w-prose">
              {t(lang, "hero_sub")}
            </p>

            {/* Modern house illustration */}
            <div className="mt-5 rounded-2xl overflow-hidden border-2 border-gold/40 bg-white/5">
              <ModernHouseSVG className="w-full h-auto block" />
            </div>

            {/* Trust badges */}
            <div className="mt-5 grid grid-cols-2 gap-2.5">
              <TrustBadge dark icon={<Users className="h-4 w-4" />} text={t(lang, "badge_arch")} />
              <TrustBadge dark icon={<Star className="h-4 w-4" />} text={t(lang, "badge_proj")} />
              <TrustBadge dark icon={<ShieldCheck className="h-4 w-4" />} text={t(lang, "badge_safe")} />
              <TrustBadge dark icon={<Languages className="h-4 w-4" />} text={t(lang, "badge_lang")} />
            </div>
          </div>
        </section>

        {/* WhatsApp chat preview */}
        <section className="mt-6">
          <div
            className="rounded-3xl border border-border bg-[#ECE5DD] p-3 sm:p-4 shadow-[0_18px_36px_-20px_rgba(11,37,69,0.25)]"
            data-testid="chat-preview"
          >
            <div className="flex items-center gap-2.5 px-1 pb-2">
              <div className="h-9 w-9 rounded-full bg-whatsapp text-white grid place-items-center shadow-sm">
                <FaWhatsapp className="h-5 w-5" />
              </div>
              <div className="leading-tight">
                <div className="text-sm font-semibold text-foreground">
                  {t(lang, "brand")} • Design Mitra
                </div>
                <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-whatsapp inline-block" />
                  {lang === "hi" ? "ऑनलाइन है" : "online now"}
                  <span className="text-foreground/60">•</span>
                  <span className="font-semibold text-foreground/80" data-testid="wa-display-chat">
                    {WA_DISPLAY}
                  </span>
                </div>
              </div>
            </div>
            <div className="space-y-2 px-1">
              <ChatBubble side="in" time={lang === "hi" ? "अभी" : "now"} testId="chat-bubble-namaste">
                {t(lang, "chat_namaste")}
              </ChatBubble>
              <ChatBubble side="in" time={lang === "hi" ? "अभी" : "now"} testId="chat-bubble-ask">
                {t(lang, "chat_ask")}
              </ChatBubble>
              <div className="pt-1">
                <TypingDots />
              </div>
            </div>
            <div className="mt-3 flex items-center gap-2 px-2 py-2 rounded-full bg-white border border-border">
              <Mic className="h-5 w-5 text-neem" />
              <ImageIcon className="h-5 w-5 text-navy" />
              <div className="flex-1 text-[13px] text-muted-foreground truncate">
                {lang === "hi" ? "वॉइस या फोटो भेजिए…" : "Send voice or photo…"}
              </div>
              <button
                onClick={onWhatsApp}
                data-testid="chat-preview-send"
                className="h-9 w-9 rounded-full bg-whatsapp text-white grid place-items-center shadow"
                aria-label="Open WhatsApp"
              >
                <FaWhatsapp className="h-5 w-5" />
              </button>
            </div>
          </div>
        </section>

        {/* 1) Aspirational — Before/After */}
        <BeforeAfter lang={lang} />

        {/* 1.4) Free Sample lead-capture banner — drives mobile-verified
            sign-ups. Hidden automatically when the campaign is disabled
            in /admin → Sample Design Settings. */}
        <FreeSampleBanner />

        {/* 1.5) Mera Naksha Design Library — ready-made designs */}
        <section className="mt-12" data-testid="store-teaser-section">
          <Link
            to="/store"
            data-testid="store-teaser-card"
            className="group block rounded-3xl bg-gradient-to-br from-navy via-navy-deep to-navy text-white p-5 sm:p-7 border border-navy-600 shadow-[0_30px_60px_-30px_rgba(11,37,69,0.65)] relative overflow-hidden hover:shadow-[0_40px_80px_-30px_rgba(11,37,69,0.75)] transition-all"
          >
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-gold-dark via-gold to-gold-dark" />
            <div className="absolute -right-12 -bottom-12 w-64 h-64 rounded-full bg-gold/10 blur-3xl pointer-events-none" />

            <div className="flex items-start gap-4 relative">
              <div className="h-14 w-14 rounded-2xl bg-gold text-navy grid place-items-center shrink-0 shadow-lg">
                <Download className="h-7 w-7" strokeWidth={2.25} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-gold/20 border border-gold/40 text-gold text-[10.5px] font-bold tracking-wider uppercase">
                  <Sparkles className="h-3 w-3" />
                  {t(lang, "store_landing_kicker")}
                </div>
                <h2 className="mt-2 text-2xl sm:text-3xl font-extrabold tracking-tight text-balance">
                  {t(lang, "store_landing_title")}
                </h2>
                <p className="mt-1.5 text-[14.5px] text-white/80 leading-relaxed max-w-prose">
                  {t(lang, "store_landing_sub")}
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {storeCount !== null && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/8 border border-white/15 text-[12px] font-bold">
                      <span className="h-1.5 w-1.5 rounded-full bg-gold" />
                      {storeCount}+ {t(lang, "store_landing_count")}
                    </span>
                  )}
                  {["18×34", "20×40", "20×50"].map((sz) => (
                    <span key={sz} className="px-2.5 py-1 rounded-full bg-gold/15 border border-gold/30 text-gold text-[12px] font-bold">
                      {sz}
                    </span>
                  ))}
                </div>
                <div className="mt-5 inline-flex items-center gap-2 min-h-[48px] px-5 rounded-xl bg-gold text-navy font-extrabold text-[14.5px] group-hover:bg-white transition-colors">
                  {t(lang, "store_landing_cta")}
                  <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </div>
              </div>
            </div>
          </Link>
        </section>

        {/* 2) Functional — Savings Calculator */}
        <SavingsCalculator lang={lang} />

        {/* Mitra Ecosystem — Bharat-first trusted professional network */}
        <section className="mt-12" data-testid="mitras-teaser-section">
          <Link
            to="/mitras"
            data-testid="mitras-teaser-card"
            className="group block rounded-3xl bg-white border-2 border-navy/15 p-5 sm:p-7 shadow-[0_24px_48px_-30px_rgba(11,37,69,0.45)] hover:shadow-[0_36px_60px_-30px_rgba(11,37,69,0.55)] hover:border-gold/60 transition-all relative overflow-hidden"
          >
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-navy via-navy-deep to-gold" />
            <div className="flex items-start gap-4 relative">
              <div className="h-14 w-14 rounded-2xl bg-navy text-gold grid place-items-center shrink-0 shadow-lg">
                <Sparkles className="h-7 w-7" strokeWidth={2.25} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-navy/8 border border-navy/15 text-navy text-[10.5px] font-bold tracking-wider uppercase">
                  Mitra Ecosystem
                </div>
                <h2 className="mt-2 text-2xl sm:text-3xl font-extrabold tracking-tight text-navy text-balance">
                  Find Your <span className="text-gold-dark">Trusted Mitras</span>
                </h2>
                <p className="mt-1.5 text-[14.5px] text-muted-foreground leading-relaxed max-w-prose">
                  Connect with experienced professionals and trusted service partners for every stage of your home journey — design, construction, property{FINANCE_MITRA_ENABLED ? ", materials and finance" : " and materials"}.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {PUBLIC_MITRA_BUCKETS.map((b) => (
                    <span key={b.key} className="px-2.5 py-1 rounded-full bg-sand-alt border border-border text-navy text-[12px] font-bold">
                      {b.label}
                    </span>
                  ))}
                </div>
                <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-gold text-navy font-extrabold text-[13.5px] group-hover:bg-gold-dark group-hover:text-white transition-colors">
                  Explore Mitras
                  <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </div>
              </div>
            </div>
          </Link>
        </section>

        {/* Visual Requirement Capture */}
        <section className="mt-12" data-testid="capture-section">
          <div className="flex items-end justify-between mb-3">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-gold-dark font-bold">
                {t(lang, "step_label")}
              </div>
              <h2 className="mt-1 text-2xl sm:text-3xl font-bold tracking-tight text-navy">
                {t(lang, "section_capture")}
              </h2>
              <p className="text-sm text-muted-foreground">
                {t(lang, "section_capture_sub")}
              </p>
            </div>
          </div>

          {/* Progress */}
          <div className="h-2 w-full rounded-full bg-sand-alt overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-navy via-navy-500 to-gold transition-all duration-500"
              style={{ width: `${stepProgress}%` }}
              data-testid="step-progress"
            />
          </div>

          {/* Zameen — Plot Dimensions (prominent L × W) */}
          <div className="mt-6">
            <div className="flex items-center gap-2 mb-3">
              <Ruler className="h-5 w-5 text-navy" />
              <h3 className="text-lg font-bold text-navy">{t(lang, "plot_dim_title")}</h3>
              <span className="text-sm text-muted-foreground">
                — {t(lang, "plot_dim_sub")}
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="plot-dim-grid">
              {PLOT_OPTIONS.map((p, i) => (
                <SelectionCard
                  key={p.id}
                  testId={`plot-option-${p.id}`}
                  selected={plot === p.id}
                  onClick={() => setPlot(p.id)}
                  icon={<PlotIcon size={28 + (i % 3) * 2} />}
                  label={p.label}
                  sublabel={`${p.sqft} ${t(lang, "sqft")}`}
                  caption={t(lang, p.captionKey)}
                />
              ))}
            </div>
          </div>

          {/* Plot Facing — Direction */}
          <div className="mt-8">
            <div className="flex items-center gap-2 mb-3">
              <Compass className="h-5 w-5 text-navy" />
              <h3 className="text-lg font-bold text-navy">{t(lang, "facing_title")}</h3>
              <span className="text-sm text-muted-foreground">
                — {t(lang, "facing_sub")}
              </span>
            </div>
            <div className="grid grid-cols-4 gap-3" data-testid="facing-grid">
              {FACING_OPTIONS.map((f) => (
                <SelectionCard
                  key={f.id}
                  testId={`facing-option-${f.id}`}
                  selected={facing === f.id}
                  onClick={() => setFacing(f.id)}
                  icon={<CompassFacingIcon angle={f.angle} size={36} />}
                  label={t(lang, f.labelKey)}
                />
              ))}
            </div>
          </div>

          {/* Kamre */}
          <div className="mt-8">
            <div className="flex items-center gap-2 mb-3">
              <BedDouble className="h-5 w-5 text-navy" />
              <h3 className="text-lg font-bold text-navy">{t(lang, "kamre_title")}</h3>
              <span className="text-sm text-muted-foreground">
                — {t(lang, "kamre_sub")}
              </span>
            </div>
            <div className="grid grid-cols-4 gap-3">
              {ROOM_OPTIONS.map((r) => (
                <SelectionCard
                  key={r.id}
                  testId={`room-option-${r.id}`}
                  selected={rooms === r.id}
                  onClick={() => setRooms(r.id)}
                  icon={<BedDouble className="h-7 w-7" strokeWidth={1.75} />}
                  label={r.label}
                  sublabel={r.sub}
                />
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="mt-12" data-testid="trust-section">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-navy">
            {t(lang, "trust_title")}
          </h2>
          <ol className="mt-4 space-y-3">
            <Step
              num="1"
              title={t(lang, "trust_step1_t")}
              desc={t(lang, "trust_step1_d")}
              tone="navy"
            />
            <Step
              num="2"
              title={t(lang, "trust_step2_t")}
              desc={t(lang, "trust_step2_d")}
              tone="gold"
            />
            <Step
              num="3"
              title={t(lang, "trust_step3_t")}
              desc={t(lang, "trust_step3_d")}
              tone="neem"
            />
          </ol>

          <div className="mt-6 px-4 py-3 rounded-2xl bg-navy-50 border border-navy-100 flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-navy" />
            <span className="text-sm font-semibold text-navy">
              {t(lang, "footer_safe")}
            </span>
          </div>
        </section>

        {/* Material Concierge teaser */}
        <section className="mt-12" data-testid="concierge-section">
          <div className="rounded-3xl bg-white border-2 border-gold/30 p-5 sm:p-6 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="h-12 w-12 rounded-2xl bg-gold/15 text-gold-dark grid place-items-center border border-gold/30 shrink-0">
                <Package className="h-6 w-6" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[11px] uppercase tracking-[0.18em] text-gold-dark font-bold">
                  {lang === "hi" ? "मटेरियल कन्सीयर्ज" : "Material Concierge"}
                </div>
                <h3 className="mt-1 text-xl sm:text-2xl font-bold tracking-tight text-navy">
                  {lang === "hi"
                    ? "नक्शा बना? अब मटेरियल भी हम से लीजिए।"
                    : "Plan ready? Buy materials from verified partner stores."}
                </h3>
                <p className="mt-1.5 text-[14px] text-muted-foreground leading-relaxed">
                  {lang === "hi"
                    ? "Satara, Gorakhpur और अधिक शहरों में Mera Naksha के Verified Partner Stores से सीमेंट, सरिया, ईंट खरीदिए — सही दाम पर, सीधे WhatsApp पर।"
                    : "Buy cement, steel and bricks from Mera Naksha verified partner stores in Satara, Gorakhpur and more cities — fair prices, directly on WhatsApp."}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-navy-50 text-navy border border-navy/15 text-[12px] font-semibold">
                    <MapPin className="h-3 w-3" /> Satara
                  </span>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-navy-50 text-navy border border-navy/15 text-[12px] font-semibold">
                    <MapPin className="h-3 w-3" /> Gorakhpur
                  </span>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gold/15 text-gold-dark border border-gold/30 text-[12px] font-semibold">
                    <ShieldCheck className="h-3 w-3" /> Verified stores
                  </span>
                </div>
                <Link
                  to="/dashboard"
                  data-testid="concierge-cta"
                  className="mt-4 inline-flex items-center gap-2 min-h-[48px] px-5 rounded-xl bg-navy text-gold font-bold text-[14px] hover:bg-navy-deep transition-colors"
                >
                  {lang === "hi" ? "पार्टनर स्टोर देखें" : "View partner stores"}
                  <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Community — WhatsApp join section */}
        <section className="mt-12" data-testid="community-section">
          <div className="relative overflow-hidden rounded-3xl bg-navy text-white p-6 sm:p-8 border border-navy-600 shadow-[0_30px_60px_-30px_rgba(11,37,69,0.65)]">
            {/* Gold accent bar */}
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-gold-dark via-gold to-gold-dark" />
            {/* WhatsApp glow */}
            <div className="absolute -right-10 -bottom-10 w-56 h-56 rounded-full bg-whatsapp/20 blur-3xl pointer-events-none" />

            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-whatsapp/15 border border-whatsapp/40 text-whatsapp-bgBorder text-[11px] font-bold tracking-wider uppercase">
              <FaWhatsapp className="h-3.5 w-3.5 text-whatsapp" />
              {t(lang, "community_eyebrow")}
            </div>

            <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight text-white text-balance">
              {t(lang, "community_title")}
            </h2>

            <p className="mt-2 text-[15px] text-white/80 leading-relaxed max-w-prose">
              {t(lang, "community_msg")}
            </p>

            <div className="mt-4 flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/8 border border-white/15 text-[12px] font-semibold">
                <Users className="h-3.5 w-3.5 text-gold" />
                {t(lang, "community_members")}
              </span>
              {["community_perk_1", "community_perk_2", "community_perk_3"].map((k) => (
                <span
                  key={k}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gold/15 border border-gold/30 text-gold text-[12px] font-semibold"
                >
                  ✓ {t(lang, k)}
                </span>
              ))}
            </div>

            <a
              href={COMMUNITY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="community-cta"
              className="mt-5 w-full sm:w-auto sm:inline-flex inline-flex items-center justify-center gap-2 min-h-[60px] px-6 rounded-2xl bg-whatsapp hover:bg-whatsapp-hover text-white font-extrabold text-[16px] shadow-[0_14px_32px_rgba(37,211,102,0.45)] active:scale-[0.99] animate-pulse-soft"
            >
              <FaWhatsapp className="h-5 w-5" />
              {t(lang, "community_cta")}
              <ChevronRight className="h-5 w-5" />
            </a>
            <div className="mt-2 text-[11.5px] text-white/55">
              {t(lang, "community_disclaimer")}
              <span className="block sm:inline sm:ml-2">
                {lang === "hi" ? "संपर्क:" : "Contact:"}{" "}
                <a
                  href={`https://wa.me/${WA_PHONE}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="wa-display-community"
                  className="text-gold font-bold hover:underline"
                >
                  {WA_DISPLAY}
                </a>
              </span>
            </div>
          </div>
        </section>

        {/* About Mera Naksha — Vision · Mission */}
        <section
          className="mt-12 rounded-3xl bg-white border-2 border-gold/30 p-5 sm:p-7 shadow-sm"
          data-testid="about-section"
        >
          <div className="flex items-start gap-3">
            <div className="h-12 w-12 rounded-2xl bg-navy text-gold grid place-items-center shrink-0 font-extrabold text-lg">
              MN
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[11px] uppercase tracking-[0.18em] text-gold-dark font-bold">
                {t(lang, "about_kicker")}
              </div>
              <h3 className="mt-1 text-xl sm:text-[26px] font-extrabold tracking-tight text-navy leading-tight">
                {t(lang, "about_headline")}
              </h3>
              <p className="mt-3 text-[14px] text-muted-foreground leading-relaxed">
                {t(lang, "about_intro")}
              </p>
            </div>
          </div>

          {/* Vision · Mission cards — nation-building positioning */}
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            <div
              className="rounded-2xl bg-navy text-white p-5 relative overflow-hidden"
              data-testid="vision-card"
            >
              <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-gold/15 blur-2xl pointer-events-none" />
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-gold font-extrabold">
                {t(lang, "vision_label")}
              </div>
              <p className="mt-2 text-[14px] leading-relaxed text-white/90 font-medium">
                {t(lang, "vision_text")}
              </p>
            </div>
            <div
              className="rounded-2xl bg-gold/15 border-2 border-gold/40 text-navy p-5"
              data-testid="mission-card"
            >
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-gold-dark font-extrabold">
                {t(lang, "mission_label")}
              </div>
              <p className="mt-2 text-[14px] leading-relaxed font-medium">
                {t(lang, "mission_text")}
              </p>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Link
              to="/about"
              data-testid="about-read-full-story"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-navy text-gold text-[12.5px] font-extrabold border-2 border-gold hover:bg-gold hover:text-navy transition-colors"
            >
              {lang === "hi" ? "हमारी पूरी कहानी पढ़ें →" : "Read our full story →"}
            </Link>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-navy/5 text-navy text-[12px] font-extrabold">
              <ShieldCheck className="h-3.5 w-3.5" />
              {lang === "hi" ? "Made in India" : "Made in India"}
            </div>
          </div>
        </section>
      </main>

      {/* Footer — brand-led, with a discreet legal line at the very bottom */}
      <footer
        className="bg-navy text-white/85 mt-12 pt-8 pb-28 sm:pb-10 px-4"
        data-testid="site-footer"
      >
        <div className="mx-auto max-w-3xl">
          <div className="flex items-start gap-3">
            <div className="h-12 w-12 rounded-2xl bg-gold text-navy grid place-items-center shrink-0 font-extrabold text-lg">
              MN
            </div>
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-[0.22em] text-gold font-bold">
                Mera Naksha
              </div>
              <div className="text-[13.5px] font-bold text-white">
                {lang === "hi"
                  ? "अपने सपनों का घर"
                  : "Apne Sapnon Ka Ghar"}
              </div>
              <div className="text-[12px] text-white/65 mt-0.5">
                {lang === "hi"
                  ? "हर गाँव, हर शहर — हर परिवार के लिए नक्शा।"
                  : "Architectural plans for every village & small town."}
              </div>
            </div>
            <a
              href={`https://wa.me/${WA_PHONE}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-wa-link"
              className="inline-flex items-center gap-1 text-[12px] font-bold text-gold hover:underline"
            >
              <FaWhatsapp className="h-3.5 w-3.5" /> {WA_DISPLAY}
            </a>
          </div>
          <div className="mt-5 pt-4 border-t border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="footer-contact-row">
            <div className="text-[11.5px] text-white/55">© {new Date().getFullYear()} Mera Naksha</div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11.5px] text-white/75">
              <div className="text-[10px] uppercase tracking-[0.2em] text-gold font-extrabold">Contact Us</div>
              <a
                href={`https://wa.me/${WA_PHONE}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-contact-whatsapp"
                className="inline-flex items-center gap-1.5 text-gold hover:underline font-bold"
              >
                <FaWhatsapp className="h-3.5 w-3.5" /> {WA_DISPLAY}
              </a>
              <a
                href={`tel:+${WA_PHONE}`}
                data-testid="footer-contact-call"
                className="inline-flex items-center gap-1.5 text-white/75 hover:text-gold font-bold"
              >
                Call {WA_DISPLAY}
              </a>
            </div>
            <div className="text-[11.5px] text-white/55">
              {lang === "hi" ? "बना है भारत में ❤️" : "Made in India ❤️"}
            </div>
          </div>
          {/* Mission Snippet — nation-building positioning, sits above the
              legal-safe disclaimer in the footer. */}
          <div
            className="mt-4 pt-4 border-t border-white/10 text-[12px] text-white/85 leading-relaxed italic max-w-prose"
            data-testid="footer-mission-snippet"
          >
            {t(lang, "mission_snippet")}
          </div>
          {/* Legal-safe disclaimer — required across product to make clear our
              designs are concept/reference deliverables only. */}
          <div className="mt-4">
            <LegalDisclaimer lang={lang} variant="footer" />
          </div>
          {/* Institutional brand sign-off — replaces the prior individual
              founder line. Kept here so the public footer reflects the
              brand-first identity per the Institutional Branding directive. */}
          <div
            className="mt-4 text-center text-[11px] text-white/65 leading-relaxed font-bold"
            data-testid="team-signoff-line"
          >
            Built by Team Mera Naksha · Designed for Indian Families
          </div>
          {/* Government registration + registered address — now that
              Mera Naksha is a govt-registered entity, GST-compliant
              display is mandatory on every public page. Kept intentionally
              small, institutional and legible, sitting just above the
              legal-nav row. */}
          <div
            className="mt-4 pt-3 border-t border-white/10 text-center text-[11px] text-white/70 leading-relaxed"
            data-testid="footer-legal-credentials"
          >
            <div className="text-[10px] uppercase tracking-[0.22em] text-gold/80 font-extrabold mb-1">
              Legal Credentials
            </div>
            <div data-testid="footer-gstin">
              <span className="text-white/45">GSTIN:</span>{" "}
              <span className="font-mono font-bold text-white/80">06DUWPK7336G1Z2</span>
            </div>
            <div data-testid="footer-udyam" className="mt-0.5">
              <span className="text-white/45">Udyam Reg:</span>{" "}
              <span className="font-mono font-bold text-white/80">UDYAM-HR-03-0161073</span>
            </div>
            <div data-testid="footer-registered-address" className="mt-0.5 max-w-prose mx-auto">
              <span className="text-white/45">Registered Address:</span>{" "}
              T12-601, RPS Savana, Sector-88, Faridabad, Haryana, 121002
            </div>
          </div>
          {/* Legal nav row — required for trust + Razorpay KYC */}
          <div
            className="mt-3 pt-3 border-t border-white/10 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-[11.5px] font-bold"
            data-testid="footer-legal-nav"
          >
            <Link to="/privacy" data-testid="footer-privacy" className="text-white/70 hover:text-gold">Privacy Policy</Link>
            <span className="text-white/30">·</span>
            <Link to="/refund" data-testid="footer-refund" className="text-white/70 hover:text-gold">Refund Policy</Link>
            <span className="text-white/30">·</span>
            <Link to="/terms" data-testid="footer-terms" className="text-white/70 hover:text-gold">Terms &amp; Conditions</Link>
            <span className="text-white/30">·</span>
            <Link to="/contact" data-testid="footer-contact" className="text-white/70 hover:text-gold">Contact Us</Link>
          </div>
        </div>
      </footer>

      {/* Sticky bottom CTA */}
      <div
        className="fixed bottom-0 left-0 right-0 z-40 bg-gradient-to-t from-sand via-sand/95 to-transparent pt-6 pb-4 px-4"
        data-testid="bottom-cta-wrap"
      >
        <div className="mx-auto max-w-md sm:max-w-3xl">
          <button
            onClick={onWhatsApp}
            disabled={!canContinue}
            data-testid="continue-whatsapp-btn"
            className={`group w-full min-h-[64px] rounded-2xl flex items-center justify-center gap-3 text-[17px] font-bold transition-all active:scale-[0.99] ${
              canContinue
                ? "bg-whatsapp text-white shadow-[0_12px_28px_rgba(37,211,102,0.35)] animate-pulse-soft"
                : "bg-sand-alt text-muted-foreground border border-border cursor-not-allowed"
            }`}
          >
            <FaWhatsapp className={`h-6 w-6 ${canContinue ? "" : "opacity-60"}`} />
            <span>
              {canContinue ? t(lang, "cta_continue") : t(lang, "cta_disabled")}
            </span>
            {canContinue && (
              <ChevronRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5" />
            )}
          </button>
          <div className="mt-2 text-center text-[11px] text-muted-foreground">
            {lang === "hi"
              ? "कोई शुल्क नहीं • रजिस्ट्रेशन की ज़रूरत नहीं"
              : "Free • No sign-up needed"}
          </div>
        </div>
      </div>
    </div>
  );
};

const TrustBadge = ({ icon, text, dark = false }) => (
  <div
    className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border shadow-sm ${
      dark
        ? "bg-white/8 border-white/15 text-white backdrop-blur-sm"
        : "bg-white border-border"
    }`}
    data-testid="trust-badge"
  >
    <span
      className={`h-7 w-7 rounded-lg grid place-items-center ${
        dark ? "bg-gold/20 text-gold" : "bg-navy-50 text-navy"
      }`}
    >
      {icon}
    </span>
    <span
      className={`text-[13px] font-semibold leading-tight ${
        dark ? "text-white" : "text-foreground"
      }`}
    >
      {text}
    </span>
  </div>
);

const Step = ({ num, title, desc, tone }) => {
  const toneMap = {
    navy: "bg-navy text-gold",
    gold: "bg-gold text-navy",
    neem: "bg-neem text-white",
  };
  return (
    <li
      className="flex gap-3 items-start p-4 rounded-2xl bg-white border border-border shadow-sm"
      data-testid={`how-step-${num}`}
    >
      <div
        className={`h-11 w-11 rounded-xl grid place-items-center font-extrabold text-lg shrink-0 border border-black/5 ${toneMap[tone]}`}
      >
        {num}
      </div>
      <div>
        <div className="text-[15px] font-bold text-foreground">{title}</div>
        <div className="text-sm text-muted-foreground leading-relaxed">{desc}</div>
      </div>
    </li>
  );
};

export default Landing;
